import React from 'react';
import { MapPin, Zap, Search, FileText, ClipboardCheck, Hammer, Settings, Building2 } from 'lucide-react';
import { SectionHeader } from '../../primitives/SectionHeader';
import { KPIBox } from '../../cards/KPIBox';
import { StepItem } from '../../cards/StepItem';
import { colors, fonts, space } from '../../tokens';
import { DraggableElement } from '../../../DraggableElement';

const PROCESS = [
  { icon: Search, num: '1', title: 'Dachanalyse', desc: 'Kostenloses Erstgutachten' },
  { icon: FileText, num: '2', title: 'Angebot', desc: 'Indikativ binnen 48h' },
  { icon: ClipboardCheck, num: '3', title: 'Vertrag', desc: 'Transparenter Dachnutzungsvertrag' },
  { icon: Settings, num: '4', title: 'Genehmigung', desc: 'Netzanschluss und Abstimmung durch uns' },
  { icon: Hammer, num: '5', title: 'Bau', desc: 'Errichtung und Anschluss der PV-Anlage' },
];

const PROCESS_ICONS = {
  search: Search,
  fileText: FileText,
  contract: ClipboardCheck,
  clipboardCheck: ClipboardCheck,
  settings: Settings,
  building: Building2,
  hammer: Hammer,
};

const resolveProcessSteps = (steps) => {
  if (!Array.isArray(steps) || steps.length === 0) {
    return PROCESS;
  }

  return steps.map((step, index) => ({
    ...step,
    icon: PROCESS_ICONS[step.icon] || PROCESS[index]?.icon || Settings,
    num: step.num || String(index + 1),
    title: step.title || PROCESS[index]?.title || '',
    desc: step.desc || PROCESS[index]?.desc || '',
  }));
};

/**
 * ProjectDataSection – "Ihr Pachtangebot" auf Page 3.
 * Enthält ProjektInfoBar, IntroText, KPIGrid (3 KPIBox), ProcessTimeline (5 StepItems).
 */
export const ProjectDataSection = ({ kundenadresse, kwp, dachflaeche, laufzeit, content = {}, layoutEditLevel = 'section', dragProps = {} }) => {
  const projectText = {
    header: {
      eyebrow: content?.header?.eyebrow || 'Ihre Anlage',
      headline: content?.header?.headline || 'IHR PACHTANGEBOT',
    },
    addressLabel: content?.addressLabel || 'Projektadresse:',
    addressTextSuffix: content?.addressTextSuffix || '- Analysierte Leistung:',
    intro: content?.intro || 'Die Voranalyse Ihrer angegebenen Dachflächen ergab, vorbehaltlich einer Vor-Ort-Besichtigung, folgende Eckdaten:',
    processLabel: content?.processLabel || 'Ablauf des Projekts',
  };
  const processSteps = resolveProcessSteps(content?.processSteps);

  return (
  <>
    {layoutEditLevel === 'detail' ? (
      <DraggableElement id="S3-Pachtangebot-Header" {...dragProps} dragAxis="y">
        <div style={{ padding: `${space.lg + space.xs}px ${space.xxl}px 0`, background: 'white' }}>
          <SectionHeader eyebrow={projectText.header.eyebrow} headline={projectText.header.headline} />
        </div>
      </DraggableElement>
    ) : (
      <div style={{ padding: `${space.lg + space.xs}px ${space.xxl}px 0`, background: 'white' }}>
        <SectionHeader eyebrow={projectText.header.eyebrow} headline={projectText.header.headline} />
      </div>
    )}

    {/* ProjektInfoBar */}
    {layoutEditLevel === 'detail' ? (
      <DraggableElement id="S3-Pachtangebot-Adresse" {...dragProps} dragAxis="y">
        <div style={{ padding: `0 ${space.xxl}px ${space.md}px`, background: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 12px', background: colors.bgSoft, borderRadius: '7px', border: `1px solid ${colors.border}` }}>
            <MapPin size={12} color={colors.orange} strokeWidth={2} />
            <p className="proposal-body" style={{ fontSize: '10px', margin: 0 }}>
              <strong style={{ color: colors.navy }}>{projectText.addressLabel}</strong>{' '}
              {kundenadresse} {projectText.addressTextSuffix} <strong style={{ color: colors.navy }}>{kwp} kWp</strong>
            </p>
          </div>
        </div>
      </DraggableElement>
    ) : (
      <div style={{ padding: `0 ${space.xxl}px ${space.md}px`, background: 'white' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '8px 12px', background: colors.bgSoft, borderRadius: '7px', border: `1px solid ${colors.border}` }}>
          <MapPin size={12} color={colors.orange} strokeWidth={2} />
          <p className="proposal-body" style={{ fontSize: '10px', margin: 0 }}>
            <strong style={{ color: colors.navy }}>{projectText.addressLabel}</strong>{' '}
            {kundenadresse} {projectText.addressTextSuffix} <strong style={{ color: colors.navy }}>{kwp} kWp</strong>
          </p>
        </div>
      </div>
    )}

    {/* IntroText + KPIGrid */}
    <div style={{ padding: `0 ${space.xxl}px ${space.md}px`, background: 'white' }}>
      {layoutEditLevel === 'detail' ? (
        <DraggableElement id="S3-Pachtangebot-Intro" {...dragProps} dragAxis="y">
          <p className="proposal-body" style={{ fontSize: '10px', marginBottom: `${space.md}px`, maxWidth: '520px' }}>
            {projectText.intro}
          </p>
        </DraggableElement>
      ) : (
        <p className="proposal-body" style={{ fontSize: '10px', marginBottom: `${space.md}px`, maxWidth: '520px' }}>
          {projectText.intro}
        </p>
      )}

      {layoutEditLevel === 'detail' ? (
        <DraggableElement id="S3-Pachtangebot-KPI-Grid" {...dragProps} dragAxis="y">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: `${space.md}px` }}>
            <DraggableElement id="S3-KPI-Dachflaeche" {...dragProps} dragAxis="y">
              <KPIBox icon={MapPin} label="Dachfläche" value={dachflaeche} valueUnit="m²" sub="Nutzbare Fläche (Voranalyse)" accentColor={colors.navy} />
            </DraggableElement>
            <DraggableElement id="S3-KPI-Leistung" {...dragProps} dragAxis="y">
              <KPIBox icon={Zap} label="Leistung" value={kwp} valueUnit="kWp" sub="Nennleistung der Anlage" accentColor={colors.orange} />
            </DraggableElement>
            <DraggableElement id="S3-KPI-Laufzeit" {...dragProps} dragAxis="y">
              <KPIBox icon={Zap} label="Laufzeit" value={laufzeit} valueUnit="Jahre" sub="+ 2 × 5 J. Verlängerungsoption" accentColor={colors.slateMid} />
            </DraggableElement>
          </div>
        </DraggableElement>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: `${space.md}px` }}>
          <KPIBox icon={MapPin} label="Dachfläche" value={dachflaeche} valueUnit="m²" sub="Nutzbare Fläche (Voranalyse)" accentColor={colors.navy} />
          <KPIBox icon={Zap} label="Leistung" value={kwp} valueUnit="kWp" sub="Nennleistung der Anlage" accentColor={colors.orange} />
          <KPIBox icon={Zap} label="Laufzeit" value={laufzeit} valueUnit="Jahre" sub="+ 2 × 5 J. Verlängerungsoption" accentColor={colors.slateMid} />
        </div>
      )}
    </div>

    {/* ProcessTimeline */}
    {layoutEditLevel === 'detail' ? (
      <DraggableElement id="S3-Prozess-Band" {...dragProps} dragAxis="y">
        <div style={{ background: colors.bgSoft, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}`, padding: `${space.md}px ${space.xxl}px` }}>
          <p style={{ fontFamily: fonts.body, fontSize: '8.5px', fontWeight: 700, color: colors.slateLight, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: `${space.sm + 2}px` }}>{projectText.processLabel}</p>
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${processSteps.length}, 1fr)`, gap: '0', position: 'relative' }}>
            <div style={{ position: 'absolute', top: '12px', left: processSteps.length > 5 ? '7%' : '10%', right: processSteps.length > 5 ? '7%' : '10%', height: '2px', background: 'linear-gradient(to right, #E07000, rgba(224,112,0,0.2))', zIndex: 0 }} />
            {processSteps.map((step, i) => (
              <DraggableElement key={i} id={`S3-Prozess-${i + 1}`} {...dragProps} dragAxis="y" initialStyle={{ position: 'relative', zIndex: 1 }}>
                <StepItem icon={step.icon} num={step.num} title={step.title} desc={step.desc} active={i < 2} />
              </DraggableElement>
            ))}
          </div>
        </div>
      </DraggableElement>
    ) : (
      <div style={{ background: colors.bgSoft, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}`, padding: `${space.md}px ${space.xxl}px` }}>
        <p style={{ fontFamily: fonts.body, fontSize: '8.5px', fontWeight: 700, color: colors.slateLight, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: `${space.sm + 2}px` }}>{projectText.processLabel}</p>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${processSteps.length}, 1fr)`, gap: '0', position: 'relative' }}>
          <div style={{ position: 'absolute', top: '12px', left: processSteps.length > 5 ? '7%' : '10%', right: processSteps.length > 5 ? '7%' : '10%', height: '2px', background: 'linear-gradient(to right, #E07000, rgba(224,112,0,0.2))', zIndex: 0 }} />
          {processSteps.map((step, i) => (
            <StepItem key={i} icon={step.icon} num={step.num} title={step.title} desc={step.desc} active={i < 2} />
          ))}
        </div>
      </div>
    )}
  </>
  );
};
